import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import {
  geocodeFailureMessage,
  shouldRunBackendGeocode,
} from '@/lib/checkout/deliveryAddress';
import { requestMenuDeliveryQuote } from '@/lib/checkoutDeliveryQuote';
import { requestMenuGeocodeAddress } from '@/lib/checkout/deliveryGeocode';
import { checkSoluGoPublicCoverage } from '@/soluqr/modules/solugo/lib/solugoCoverage';
import { detectDeliveryZone } from '@/lib/deliveryZones';
import { DEMO_CHECKOUT_DEFAULT_ADDRESS } from '@/lib/demo/demoBusiness';

import { DEMO_DELIVERY_BY_COMMUNE } from './checkoutConstants';

/**
 * Cotización de delivery (mapa + API o zonas por dirección) y estado demo.
 */
export function useCheckoutDelivery({
  isDemoCheckout,
  useAddressZonesForDelivery,
  serviceType,
  businessId,
  business,
  address,
  markerPosition,
  setMarkerPosition,
  setAddress,
}) {
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [solugoCoverage, setSolugoCoverage] = useState(null);
  const [solugoCoverageLoading, setSolugoCoverageLoading] = useState(false);

  const deliveryQuoteGenRef = useRef(0);
  const solugoCoverageGenRef = useRef(0);
  const solugoCoverageDebounceRef = useRef(null);
  const solugoCoverageAbortRef = useRef(null);
  const deliveryDebounceRef = useRef(null);
  const deliveryAbortRef = useRef(null);
  const geocodeDebounceRef = useRef(null);
  const geocodeAbortRef = useRef(null);
  const zoneDebounceRef = useRef(null);

  useEffect(() => {
    if (!isDemoCheckout) return;
    setAddress(DEMO_CHECKOUT_DEFAULT_ADDRESS);
    setMarkerPosition({ lat: -33.4372, lng: -70.6376 });
    setDeliveryLoading(false);
    setDeliveryQuote({
      delivery_fee: 2490,
      distance_km: null,
      within_radius: true,
      distance_source: 'demo',
    });
  }, [isDemoCheckout, setMarkerPosition, setAddress]);

  useEffect(() => {
    const clearGeocodeDebounce = () => {
      if (geocodeDebounceRef.current) {
        clearTimeout(geocodeDebounceRef.current);
        geocodeDebounceRef.current = null;
      }
    };

    if (
      !shouldRunBackendGeocode({
        isDemoCheckout,
        useAddressZonesForDelivery,
        serviceType,
        businessId,
        address,
        markerPosition,
      })
    ) {
      clearGeocodeDebounce();
      geocodeAbortRef.current?.abort();
      geocodeAbortRef.current = null;
      setGeocodeLoading(false);
      return;
    }

    const addr = String(address || '').trim();

    clearGeocodeDebounce();
    geocodeDebounceRef.current = setTimeout(() => {
      geocodeDebounceRef.current = null;

      geocodeAbortRef.current?.abort();
      const ac = new AbortController();
      geocodeAbortRef.current = ac;

      setGeocodeLoading(true);

      (async () => {
        try {
          const data = await requestMenuGeocodeAddress(businessId, addr, { signal: ac.signal });
          if (ac.signal.aborted) return;
          const lat = Number(data?.lat);
          const lng = Number(data?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            toast.error('No pudimos ubicar esa dirección');
            return;
          }
          // Solo pin: no reescribir el texto tipado (evita que el campo “se rellene” solo).
          setMarkerPosition({ lat, lng });
        } catch (e) {
          if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') {
            return;
          }
          toast.error(geocodeFailureMessage(e));
        } finally {
          if (!ac.signal.aborted) {
            setGeocodeLoading(false);
          }
        }
      })();
    }, 900);

    return () => {
      clearGeocodeDebounce();
      geocodeAbortRef.current?.abort();
      geocodeAbortRef.current = null;
    };
  }, [
    isDemoCheckout,
    useAddressZonesForDelivery,
    serviceType,
    address,
    markerPosition,
    businessId,
    setMarkerPosition,
  ]);

  useEffect(() => {
    const clearDebounce = () => {
      if (deliveryDebounceRef.current) {
        clearTimeout(deliveryDebounceRef.current);
        deliveryDebounceRef.current = null;
      }
    };

    if (isDemoCheckout) {
      clearDebounce();
      deliveryAbortRef.current?.abort();
      deliveryAbortRef.current = null;
      setDeliveryLoading(false);
      if (serviceType === 'delivery') {
        setDeliveryQuote({
          delivery_fee: 2490,
          distance_km: null,
          within_radius: true,
          distance_source: 'demo',
        });
      } else {
        setDeliveryQuote(null);
      }
      return;
    }

    if (useAddressZonesForDelivery) {
      return;
    }

    if (serviceType !== 'delivery') {
      clearDebounce();
      deliveryAbortRef.current?.abort();
      deliveryAbortRef.current = null;
      setDeliveryQuote(null);
      setDeliveryLoading(false);
      return;
    }
    if (!markerPosition || !businessId) {
      clearDebounce();
      deliveryAbortRef.current?.abort();
      deliveryAbortRef.current = null;
      setDeliveryQuote(null);
      setDeliveryLoading(false);
      return;
    }

    const lat = markerPosition.lat;
    const lng = markerPosition.lng;
    if (typeof lat !== 'number' || typeof lng !== 'number' || Number.isNaN(lat) || Number.isNaN(lng)) {
      return;
    }

    clearDebounce();

    deliveryDebounceRef.current = setTimeout(() => {
      deliveryDebounceRef.current = null;

      deliveryAbortRef.current?.abort();
      const ac = new AbortController();
      deliveryAbortRef.current = ac;

      const myGen = ++deliveryQuoteGenRef.current;
      setDeliveryLoading(true);

      (async () => {
        try {
          const data = await requestMenuDeliveryQuote(businessId, lat, lng, { signal: ac.signal });
          if (myGen !== deliveryQuoteGenRef.current) return;

          if (!data.within_radius) {
            toast.error(data.detail || 'Fuera del radio de cobertura');
            setMarkerPosition(null);
            setDeliveryQuote(null);
            return;
          }

          setDeliveryQuote({
            delivery_fee: typeof data.delivery_fee === 'number' ? data.delivery_fee : null,
            distance_km: typeof data.distance_km === 'number' ? data.distance_km : null,
            within_radius: data.within_radius !== false,
            distance_source: data.distance_source ?? null,
          });
        } catch (e) {
          if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') {
            return;
          }
          if (myGen !== deliveryQuoteGenRef.current) return;
          const msg = e.response?.data?.detail;
          toast.error(typeof msg === 'string' ? msg : 'No se pudo calcular el delivery');
          setDeliveryQuote(null);
        } finally {
          if (myGen === deliveryQuoteGenRef.current) {
            setDeliveryLoading(false);
          }
        }
      })();
    }, 950);

    return () => {
      clearDebounce();
      deliveryAbortRef.current?.abort();
      deliveryAbortRef.current = null;
    };
  }, [
    isDemoCheckout,
    useAddressZonesForDelivery,
    markerPosition?.lat,
    markerPosition?.lng,
    serviceType,
    businessId,
    business?.is_demo,
    setMarkerPosition,
  ]);

  useEffect(() => {
    if (isDemoCheckout) {
      if (zoneDebounceRef.current) {
        clearTimeout(zoneDebounceRef.current);
        zoneDebounceRef.current = null;
      }
      setDeliveryLoading(false);
      if (serviceType === 'delivery') {
        setDeliveryQuote({
          source: 'demo',
          within_radius: true,
          no_zone_match: false,
          delivery_fee: 2490,
          estimated_minutes: 30,
          zone_name: 'Providencia',
          distance_km: null,
          distance_source: 'demo',
        });
      } else {
        setDeliveryQuote(null);
      }
      return;
    }

    if (!useAddressZonesForDelivery) {
      return;
    }

    if (serviceType !== 'delivery' || !business) {
      if (serviceType !== 'delivery') {
        if (zoneDebounceRef.current) {
          clearTimeout(zoneDebounceRef.current);
          zoneDebounceRef.current = null;
        }
        setDeliveryQuote(null);
      }
      return;
    }

    if (zoneDebounceRef.current) {
      clearTimeout(zoneDebounceRef.current);
      zoneDebounceRef.current = null;
    }

    const zones = business.delivery_zones;
    const addr = String(address || '').trim();
    if (!addr) {
      setDeliveryQuote(null);
      return;
    }

    zoneDebounceRef.current = setTimeout(() => {
      zoneDebounceRef.current = null;
      const zone = detectDeliveryZone(address, zones);
      if (zone) {
        const price = Number(zone.price);
        const mins = Number(zone.estimated_minutes);
        setDeliveryQuote({
          source: 'zone',
          within_radius: true,
          no_zone_match: false,
          delivery_fee: Number.isFinite(price) ? price : 0,
          estimated_minutes: Number.isFinite(mins) ? mins : 0,
          zone_name: String(zone.name || '').trim() || 'Zona',
          distance_km: null,
          distance_source: 'zone',
        });
      } else if (addr.length > 2) {
        setDeliveryQuote({
          source: 'zone',
          within_radius: false,
          no_zone_match: true,
          delivery_fee: null,
          distance_km: null,
          distance_source: 'zone',
        });
      } else {
        setDeliveryQuote(null);
      }
    }, 500);

    return () => {
      if (zoneDebounceRef.current) {
        clearTimeout(zoneDebounceRef.current);
        zoneDebounceRef.current = null;
      }
    };
  }, [isDemoCheckout, useAddressZonesForDelivery, serviceType, address, business, businessId]);

  useEffect(() => {
    const clearSolugoDebounce = () => {
      if (solugoCoverageDebounceRef.current) {
        clearTimeout(solugoCoverageDebounceRef.current);
        solugoCoverageDebounceRef.current = null;
      }
    };

    if (
      isDemoCheckout ||
      !business?.solugo_fulfills_delivery ||
      serviceType !== 'delivery'
    ) {
      clearSolugoDebounce();
      solugoCoverageAbortRef.current?.abort();
      solugoCoverageAbortRef.current = null;
      setSolugoCoverage(null);
      setSolugoCoverageLoading(false);
      return;
    }

    const addr = String(address || '').trim();
    const lat = markerPosition?.lat;
    const lng = markerPosition?.lng;
    const hasCoords =
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      Number.isFinite(lat) &&
      Number.isFinite(lng);

    if (!addr && !hasCoords) {
      clearSolugoDebounce();
      solugoCoverageAbortRef.current?.abort();
      solugoCoverageAbortRef.current = null;
      setSolugoCoverage(null);
      setSolugoCoverageLoading(false);
      return;
    }

    clearSolugoDebounce();
    solugoCoverageDebounceRef.current = setTimeout(() => {
      solugoCoverageDebounceRef.current = null;

      solugoCoverageAbortRef.current?.abort();
      const ac = new AbortController();
      solugoCoverageAbortRef.current = ac;

      const myGen = ++solugoCoverageGenRef.current;
      setSolugoCoverageLoading(true);

      (async () => {
        try {
          const result = await checkSoluGoPublicCoverage({
            address: addr || undefined,
            lat: hasCoords ? lat : undefined,
            lng: hasCoords ? lng : undefined,
            signal: ac.signal,
          });
          if (myGen !== solugoCoverageGenRef.current) return;
          setSolugoCoverage(result);
        } catch (e) {
          if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') {
            return;
          }
          if (myGen !== solugoCoverageGenRef.current) return;
          setSolugoCoverage({
            inCoverage: false,
            reason: 'No se pudo verificar cobertura SoluGo en este momento.',
            unavailable: true,
          });
        } finally {
          if (myGen === solugoCoverageGenRef.current) {
            setSolugoCoverageLoading(false);
          }
        }
      })();
    }, 700);

    return () => {
      clearSolugoDebounce();
      solugoCoverageAbortRef.current?.abort();
      solugoCoverageAbortRef.current = null;
    };
  }, [
    isDemoCheckout,
    business?.solugo_fulfills_delivery,
    serviceType,
    address,
    markerPosition?.lat,
    markerPosition?.lng,
  ]);

  const demoDeliveryMeta = DEMO_DELIVERY_BY_COMMUNE.Providencia;

  const solugoFulfillsDelivery = Boolean(business?.solugo_fulfills_delivery);
  const solugoDeliveryOutOfCoverage =
    solugoFulfillsDelivery &&
    serviceType === 'delivery' &&
    solugoCoverage &&
    !solugoCoverage.unavailable &&
    solugoCoverage.inCoverage === false;
  const solugoCoverageChecking =
    solugoFulfillsDelivery && serviceType === 'delivery' && solugoCoverageLoading;

  return {
    deliveryQuote,
    deliveryLoading,
    geocodeLoading,
    solugoCoverage,
    solugoCoverageLoading,
    solugoDeliveryOutOfCoverage,
    solugoCoverageChecking,
    demoDeliveryMeta,
  };
}
